import { Injectable } from '@angular/core';
import { FeatureFlagActions, FeatureFlagSelectors } from '@next/core-lib/featureflag';
import { I18nActions, I18nSelectors } from '@next/core-lib/i18n';
import { NavigationActions } from '@next/core-lib/navigation';
import {
  AccessRoleTypes,
  Location,
  ShipToIdentification,
  UserActions,
  UserSelectors,
} from '@next/core-lib/user';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';

import * as AppActions from './actions';
import { CustomerBrsNumber } from '../shared/models/shared.models';
import { NavigationItemsService } from '../shared/services/navigation-items.service';

@Injectable()
export class AppEffects {
  getUserNavigationItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        UserActions.customerInitialized,
        I18nActions.localeInitialized,
        UserActions.customerChanged,
        FeatureFlagActions.getFeatureFlagsRequestSuccess,
      ),
      debounceTime(200),
      withLatestFrom(
        this.store.select(UserSelectors.selectCustomer),
        this.store.select(UserSelectors.selectUserApplications),
        this.store.select(I18nSelectors.selectLocale),
        this.store.select(FeatureFlagSelectors.selectFeatureFlagState),
      ),
      filter(
        ([_, customer, userApplications, selectedLocale]) =>
          customer?.id != null && !!selectedLocale && !!userApplications?.length,
      ),
      map(([_, customer, userApplications, selectedLocale, featureFlagState]) => {
        const authorizedUserApplications = userApplications.filter(
          (userApplication) => userApplication.RoleKey !== AccessRoleTypes.NotAuthorized,
        );
        const applicationSignature = authorizedUserApplications
          .map((userApplication) => `${userApplication.ApplicationKey}:${userApplication.RoleKey}`)
          .sort((first, second) => first.localeCompare(second))
          .join(',');
        const featureFlagSignature = [...(featureFlagState?.featureFlags ?? [])]
          .sort((first, second) => first.localeCompare(second))
          .join(',');

        return {
          authorizedUserApplications,
          selectedLocale,
          signature: `${customer.id}|${selectedLocale}|${featureFlagState?.salesArea?.toLowerCase() ?? ''}|${featureFlagSignature}|${applicationSignature}`,
        };
      }),
      filter(({ authorizedUserApplications }) => authorizedUserApplications.length > 0),
      distinctUntilChanged((previous, current) => previous.signature === current.signature),
      switchMap(({ authorizedUserApplications, selectedLocale }) =>
        this.navigationItemsService.getMatchingNavigationItems(
          authorizedUserApplications,
          selectedLocale,
        ),
      ),
      map((navigationItems) =>
        NavigationActions.setNavigationItems({
          navigationItems,
        }),
      ),
    ),
  );

  getCustomerKvkNumbers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.customerInitialized, UserActions.customerChanged),
      debounceTime(200),
      withLatestFrom(this.store.select(UserSelectors.selectCustomer)),
      map(([_, customer]) =>
        AppActions.getCustomerKvkNumbersSuccess({
          customerKvkNumbers: customer?.shipTos
            .map((location) =>
              location.identifications
                ?.filter(
                  (identification) =>
                    identification.name === ShipToIdentification.BUP002 &&
                    new Date(identification.validFrom) <= new Date() &&
                    (!identification.validTo || new Date(identification.validTo) >= new Date()),
                )
                .reduce(
                  (acc, identification) => [
                    ...acc,
                    {
                      relatedLocation: location,
                      kvkNumber: identification.value,
                    },
                  ],
                  [],
                ),
            )
            .flat(),
        }),
      ),
    ),
  );

  getCustomerBrsNumbers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.customerInitialized, UserActions.customerChanged),
      debounceTime(200),
      withLatestFrom(this.store.select(UserSelectors.selectCustomer)),
      map(([_, customer]) => {
        const customerBrsNumbers = customer?.shipTos
          .map((location) =>
            location.identifications
              ?.filter((identification) => identification.name === ShipToIdentification.ZBRS01)
              .reduce(
                (acc, identification) => [
                  ...acc,
                  {
                    relatedLocation: location,
                    brsNumber: identification.value,
                    validFrom: new Date(identification.validFrom),
                    validTo: new Date(identification.validTo),
                  },
                ],
                [],
              ),
          )
          .flat()
          .reduce((acc, customerBrsNumber) => {
            const existing = acc.find(
              (accObject: CustomerBrsNumber) => accObject.brsNumber === customerBrsNumber.brsNumber,
            );

            if (existing) {
              const existingLocation = existing.relatedLocations.some(
                (location: Location) =>
                  location.externalId === customerBrsNumber.relatedLocation.externalId,
              );

              if (!existingLocation) {
                acc
                  .find(
                    (accObject: CustomerBrsNumber) =>
                      accObject.brsNumber === customerBrsNumber.brsNumber,
                  )
                  .relatedLocations.push(customerBrsNumber.relatedLocation);
              }
            } else {
              acc = [
                ...acc,
                {
                  relatedLocations: [customerBrsNumber.relatedLocation],
                  brsNumber: customerBrsNumber.brsNumber,
                  validFrom: new Date(customerBrsNumber.validFrom),
                  validTo: new Date(customerBrsNumber.validTo),
                },
              ];
            }

            return acc;
          }, []);

        // Sort the BRS numbers descending by expiration date, then by start date
        customerBrsNumbers.sort((brs1: CustomerBrsNumber, brs2: CustomerBrsNumber) => {
          if (brs1.validTo === brs2.validTo) {
            return brs2.validFrom.getTime() - brs1.validFrom.getTime();
          }

          return brs2.validTo.getTime() - brs1.validTo.getTime();
        });

        return AppActions.getCustomerBrsNumbersSuccess({
          customerBrsNumbers,
        });
      }),
    ),
  );

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly navigationItemsService: NavigationItemsService,
  ) {}
}
